import { h, Component } from 'preact';
import Swipeable from '../swipeable';
import Spinner from '../spinner';
import Icon from '../icon';
import cx from 'classnames';
import style from './style';

const supportsTransforms = () => (
	typeof document!=='undefined' && 'transform' in document.body.style
);
const UPDATE_TIMEOUT = 1000;
const THROTTLE = 66;

export default class Carousel extends Component {
	static defaultProps = {
		showScrollButtons: true
	}

	setPrevArrowRef = (prevArrow) => { this.prevArrow = prevArrow; }
	setNextArrowRef = (nextArrow) => { this.nextArrow = nextArrow; }

	/**
	 * Keep track of the last offsetInfo.end
	 */
	lastOffsetEnd = false;

	/**
	 * Get the children of the inner container
	 * @return {Object}
	 */
	getChildren() {
		let children = this.inner && this.inner.childNodes;

		if (children && children.length === 1) {
			return [].slice.call(this.inner.querySelectorAll('[data-slide-content] [data-slide-child]'));
		}

		return children && [].slice.call(children) || [];
	}

	/**
	 * Get the number of children in view
	 * @return {Integer}
	 */
	getChildCount() {
		let children = this.getChildren();

		if (!children) {
			return 0;
		}

		return children.length;
	}

	/**
	 * Get the number of children we are supposed to advance by on each click
	 * @return {Integer}
	 */
	getSlideLength() {
		return this.props.slideLength || 1;
	}

	/**
	 * Get the maximum index position we should slide to
	 * @return {Integer}
	 */
	getMaxPosition() {
		let length = this.getSlideLength(),
			maxPosition = this.getChildCount() - length;

		if (length > 1) {
			maxPosition--;
		}

		return maxPosition;
	}

	/**
	 * Get the maximum offset the carousel can scroll
	 * @return {Integer}
	 */
	getMaxOffset() {
		const children = this.getChildren(),
			innerWidth = children.reduce((width, child) => width + child.offsetWidth, 0),
			controlsWidth = (this.nextArrow ? this.nextArrow.offsetWidth : 0) + (this.prevArrow ? this.prevArrow.offsetWidth : 0),
			carouselWidth = this.carouselElem.offsetWidth;

		if (innerWidth <= carouselWidth - controlsWidth) {
			return 0;
		}

		return -1 * (innerWidth - carouselWidth + controlsWidth);
	}

	/**
	 * Get information on the current offset
	 * @param {Integer}
	 * @return {Object}
	 *   - offset: number - the current offset
	 *   - end: boolean - whether or not carousel has reached the end of its children
	 */
	getChildOffset(index) {
		const children = this.getChildren(),
			innerOffset = this.inner && this.inner.getBoundingClientRect().left;

		if (!children || !children.length) return { offset: 0, end: true };

		let pos = Math.min(index, children.length - 1),
			offset = children[pos] ? -(children[pos].getBoundingClientRect().left - innerOffset) : 0,
			end = false;

		if (this.inner && children[pos]) {
			const maxOffset = this.getMaxOffset();
			if (offset < maxOffset || maxOffset === 0) {
				offset = maxOffset;
				end = true;
			}
		}

		return { offset, end };
	}

	/**
	 * Update the view position
	 */
	scroll = ({ offset }) => {
		let previous = this.state.index || 0,
			index = previous + (+offset || 0);
		index = Math.max(0, Math.min(this.getMaxPosition(), index));
		this.setState({ index });
		return index!==previous;
	};

	/**
	 * Update the view position to the start of the content
	 */
	scrollToStart = () => {
		this.setState({ index: 0 });
	}

	/**
	 * When beginning a swipe, store the stylesheet-defined CSS values to be reverted to after.
	 */
	maxOffset = null;

	/**
	 * When beginning a swipe, store the stylesheet-defined CSS values to be reverted to after.
	 */
	onSwipeStart = () => {
		this.swipeLeft = supportsTransforms() ? (this.inner.style.transform || '') : parseFloat(this.inner.style.left);
		this.swipeTransition = this.inner.style.transition || '';
		this.inner.style.transition = 'none';
		this.maxOffset = this.getMaxOffset();
	};

	/**
	 * During swipe gestures, manually apply the swipe offset as a transform
	 * Ensure a carousel does not swipe past content leaving errant whitespace.
	 */
	onSwiping = ({ deltaX }) => {
		if (this.maxOffset === 0) {
			return;
		}

		if (supportsTransforms()) {
			const swipeLeft = parseFloat(this.swipeLeft.replace('translateX(', '').replace('px)', '') || 0),
				currentOffset = deltaX + swipeLeft;

			if ((currentOffset <= 0) && (currentOffset >= this.maxOffset)) {
				this.inner.style.transform = `translateX(${ currentOffset }px)`;
			}
		}
		else {
			this.inner.style.left = (this.swipeLeft + deltaX) + 'px';
		}
	};

	getAvgChildWidth = () => {
		if (!this.avgChildWidth) {
			let children = this.getChildren();
			this.avgChildWidth = children.reduce((width, child) => width + child.offsetWidth, 0) / children.length;
		}
		return this.avgChildWidth;
	};

	/**
	 * When finishing a swipe, scroll by an offset calculated from distance + velocity
	 */
	onSwipeEnd = e => {
		if (this.maxOffset === 0) {
			return;
		}

		const avgChildWidth = this.getAvgChildWidth(),
			factor = Math.round(Math.max(1, e.velocity) * e.deltaX / (avgChildWidth / 2)),
			offset = (e.direction==='right' ? -1 : e.direction==='left' ? 1 : 0) * Math.abs(factor);

		this.inner.style.transition = this.swipeTransition;
		if (!e.aboveThreshold || !offset || !this.scroll({ offset })) {
			this.inner.style[supportsTransforms() ? 'transform' : 'left'] = this.swipeLeft || '';
		}

		const dist = parseFloat((supportsTransforms() ? this.inner.style.transform : this.inner.style.left).replace('translateX(', '').replace('px)', ''));
		const maxOffset = this.getMaxOffset();

		if (dist < maxOffset) {
			this.swipeLeft = supportsTransforms() ? `translateX(${maxOffset}px)` : `${maxOffset}px`;
			if (supportsTransforms()) {
				this.inner.style.transform = this.swipeLeft;
			}
			else {
				this.inner.style.left = this.swipeLeft;
			}
		}
	};

	mountTimeout = null;
	updateTimeout = null;
	resizeTimeout = null;

	/**
	 * Infinite loops are bad, lets try to prevent them by not allowing forcedUpdates
	 * in componentDidUpdate in certain circumstances.
	 */
	allowForcedUpdate = true;

	/**
	 * Throttle the resize event
	 */
	resizeThrottler = () => {
		if ( !this.resizeTimeout ) {
			this.resizeTimeout = setTimeout( () => {
				this.resizeTimeout = null;
				this.resizeHandler();
			}, THROTTLE);
		}
	}

	/**
	 * Update the carousel on resize
	 */
	resizeHandler = () => {
		this.forceUpdate();
	}

	/**
	 * Force an update on the first render to ensure the ref is available.
	 * setTimeout is needed to ensure children have rendered before re-rendering, so that width calculations are accurate.
	 */
	componentDidMount() {
		this.mountTimeout = setTimeout(() => {
			this.forceUpdate();
		}, UPDATE_TIMEOUT);
		window.addEventListener('resize', this.resizeThrottler, false);
	}

	/**
	 * There appears to be timing issues with the rendering and calculating the offset.  After each update
	 * do a delayed check of the last offsetInfo.end value to see if it has changed and if so force and update.
	 */
	componentDidUpdate() {
		clearTimeout(this.updateTimeout);

		this.updateTimeout = setTimeout(() => {
			let offsetInfo = this.getChildOffset(this.state.index);

			if (offsetInfo.end !== this.lastOffsetEnd && this.allowForcedUpdate) {
				this.forceUpdate();
				this.allowForcedUpdate = false;
			}
			else {
				this.allowForcedUpdate = true;
			}
		}, UPDATE_TIMEOUT);
	}

	/**
	 * Remove the resize handler
	 */
	componentWillUnmount() {
		clearTimeout(this.mountTimeout);
		clearTimeout(this.updateTimeout);

		window.removeEventListener('resize', this.resizeThrottler, false);
	}

	render({ children, noItemsMsg, showLoading = true, showMsg = false, showScrollButtons, ...props }, { index = 0 }) {
		let offsetInfo = this.getChildOffset(index),
			css = supportsTransforms() ? `transform: translateX(${offsetInfo.offset}px)` : `left: ${offsetInfo.offset}px;`;

		this.lastOffsetEnd = offsetInfo.end;

		return (
			<div {...props} class={cx(style.carousel, props.class)} ref={(carouselElem) => { this.carouselElem = carouselElem; }}>
				{showScrollButtons && (
					<Button class={style.prev} onClick={this.scroll} offset={-1} disabled={index===0} refCallback={this.setPrevArrowRef}>
						<Icon name="navigate-left-cube" />
					</Button>
				)}

				<Swipeable onStart={this.onSwipeStart} onChange={this.onSwiping} onEnd={this.onSwipeEnd} distanceThreshold={50} preventDefault>
					<div class={cx(style.innerContainer, !showScrollButtons && style.noButtons)}>
						<div ref={c => this.inner=c} class={style.carouselInner} style={css}>{children}</div>
					</div>
				</Swipeable>

				{showScrollButtons && (
					<Button class={style.next} onClick={this.scroll} offset={1} disabled={offsetInfo.end} refCallback={this.setNextArrowRef}>
						<Icon name="navigate-right-cube" />
					</Button>
				)}

				<div class={cx(style.empty, showMsg && style.showMsg)}>{noItemsMsg}</div>

				{
					showLoading && !(children && children.length) && <Spinner class={style.loadingIndicator} />
				}
			</div>
		);
	}
}

/**
 * Render a button for sliding left/right
 */
class Button extends Component {
	click = (e) => {
		this.props.onClick(this.props);
	};

	render({ offset, onClick, refCallback, ...props }) {
		return (
			<button
				{...props}
				ref={refCallback}
				onClick={this.click}
				class={cx(style.button, props.class)}
			/>
		);
	}
}
